import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { useDrag } from 'react-dnd';
import { tableHelper } from '@builttocreate/engine-utils';

import TableEditor from './TableEditor';
import Icon from '../../misc/Icon';
import Accordion from '../../misc/Accordion';

import FieldTypes from '../../../constants/FieldTypes';
import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';
import FileViews from '../../../constants/FileViews';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

const {
  getDefaultRow,
  getRowOrder,
  getColumnOrder,
  getDefaultTextColumn,
  getDefaultDropdownColumn,
  getDefaultImageColumn,
  handleMoveColumnLeft,
  handleMoveColumnRight
} = tableHelper;

const StyledAccordionContent = styled.div`
  display: relative;
  display: flex;
  flex-direction: row;
  flex: 1;
  padding: 8px;
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 0px;
`;

const StyledLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: gray8;
  flex: 1;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width:${(props) => (props.view === FileViews.mobile ? '280px' : '480px')};
  color: ${(props) => props.theme.fontPrimaryColor};
  svg {
    color: ${(props) => props.theme.fontPrimaryColor} !important;
    font-size: 15px;
  }
`;

const StyledLabelIcon = styled.span`
  margin-right: 2px;
`;

const StyledDescription = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${(props) => props.theme.fontColorSecondary};
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: ${(props) => props.theme.borderRadius}px;
  overflow: auto;
`;

const StyledDragHandler = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: ${(props) => props.theme.borderRadius}px;
  font-size: 12px;
  color: ${(props) => props.theme.borderColor};
  padding: 4px 8px;
  font-weight: 600;
  margin-right: 4px;
  transition: all 0.15s ease-in-out;

  svg {
    color: ${(props) => props.theme.borderColor};
  }

  :hover {
    cursor: grab;
    border: 1px solid ${(props) => props.theme.primaryColor};
    svg {
      color: ${(props) => props.theme.primaryColor};
    }
  };
`;

const TableEditorSettings = (props) => {

  const theme = useContext(ThemeContext);

  const { id, activeFieldPosition, display, onChange, disableDragAndDrop, view } = props;

  const {
    _id,
    title,
    field,
    identifier,
    value,
    rowOrder,
    tableColumns,
    tableColumnOrder
  } = activeFieldPosition || {};

  const [open, setOpen] = useState(display);

  /**
   * Important Note:
   *
   * activeFieldPosition Explanation
   *
   * We need the activeField for table type fields because there can be many fieldPostions for a single table field,
   * we link the all the associated fieldPostions back to the original field using the `fieldPosition.field` property.
   */
  const entireTableFieldPosition = {
    ...activeFieldPosition,
    type: FieldTypes.table,
    displayType: FieldDisplayTypes.original,
    width: 400,
    height: 200,
  };

  const [collectedProps, drag] = useDrag({
    type: FieldTypes.table,
    item: entireTableFieldPosition
  });

  return (
    <Accordion
      isOpen={open}
      height={240}
      onClick={() => setOpen(!open)}
      title={(
        <StyledHeader>
          <StyledLabel view={view} theme={theme}>
            <StyledLabelIcon><Icon icon={ThemeIcons.settings.settings} size="lg" /></StyledLabelIcon>
            {' '}
            {title || 'Field Settings'}
          </StyledLabel>
          {disableDragAndDrop || !open ? null : (
            <StyledDescription theme={theme}>
              Drag individual cells onto the page using the drag icon.
            </StyledDescription>
          )}
        </StyledHeader>
      )}
    >
      <StyledAccordionContent>
        {disableDragAndDrop ? null : (
          <StyledDragHandler theme={theme} {...collectedProps} ref={drag} data-testid={`${id}-table-settings-drag`}>
            <Icon icon={ThemeIcons.misc.drag} />
          </StyledDragHandler>
        )}
        <StyledWrapper theme={theme}>
          <TableEditor
            {...props}
            fieldId={_id}
            onRowCreate={() => handleRowCreate(field, identifier, _id, value, rowOrder, tableColumns, onChange)}
            onRowDelete={(id, rowId) => handleRowDelete(field, identifier, _id, rowId, value, rowOrder, onChange)}
            onColumnCreate={(id, type) => handleColumnCreate(field, identifier, _id, type, tableColumns, tableColumnOrder, onChange)}
            onColumnMoveLeft={(id, columnId) => handleColumnMoveLeft(field, identifier, _id, columnId, tableColumns, tableColumnOrder, onChange)}
            onColumnMoveRight={(id, columnId) => handleColumnMoveRight(field, identifier, _id, columnId, tableColumns, tableColumnOrder, onChange)}
          />
        </StyledWrapper>
      </StyledAccordionContent>
    </Accordion>
  );
};

export const handleRowCreate = (fieldId, fieldIdentifier, fieldPositionId, rows = [], rowOrder, tableColumns, onChange) => {

  const newRow = getDefaultRow();

  //Logic to add default value to newly created row
  tableColumns.forEach((column) => {
    if (column.deleted) return;
    newRow.cells[column._id] = column.value || '';
  });

  //Note: Use rows and NOT nextRows in the getRowOrder function.
  const nextRowOrder = rowOrder ? [...rowOrder] : getRowOrder(rowOrder, rows);
  nextRowOrder.push(newRow._id);

  const nextRows = [...rows];
  nextRows.push(newRow);

  onChange(fieldId, fieldIdentifier, fieldPositionId, { value: nextRows, rowOrder: nextRowOrder });

};

export const handleRowDelete = (fieldId, fieldIdentifier, fieldPositionId, rowId, rows = [], rowOrder, onChange) => {

  //Note: Use rows and NOT nextRows in the getRowOrder function.
  const nextRowOrder = rowOrder ? [...rowOrder] : getRowOrder(rowOrder, rows);
  const rowOrderIndex = nextRowOrder.indexOf(rowId);
  if (rowOrderIndex !== -1) nextRowOrder.splice(rowOrderIndex, 1);

  const nextRows = [...rows];
  const rowIndex = nextRows.findIndex((row) => row._id === rowId);
  if (rowIndex !== -1) nextRows[rowIndex] = { ...nextRows[rowIndex], deleted: true };

  onChange(fieldId, fieldIdentifier, fieldPositionId, { value: nextRows, rowOrder: nextRowOrder });

};

export const handleColumnCreate = (fieldId, fieldIdentifier, fieldPositionId, type, tableColumns, tableColumnOrder, onChange) => {

  //Note: Use columns and NOT nextColumns in the getColumnOrder function.
  const nextColumnOrder = tableColumnOrder ? [...tableColumnOrder] : getColumnOrder(tableColumnOrder, tableColumns);
  const nextColumns = [...tableColumns];

  let newColumn = null;

  if (type === FieldTableColumnTypes.text) {

    newColumn = getDefaultTextColumn({ width: 0 });

  } else if (type === FieldTableColumnTypes.dropdown) {

    newColumn = getDefaultDropdownColumn({ width: 0 });

  } else if (type === FieldTableColumnTypes.image) {

    const columnData = getDefaultImageColumn({ width: 0 });
    delete columnData.width;
    delete columnData.maxImageWidth;
    delete columnData.maxImageHeight;

    newColumn = columnData;

  }

  nextColumns.push(newColumn);
  nextColumnOrder.push(newColumn._id);

  onChange(fieldId, fieldIdentifier, fieldPositionId, { tableColumns: nextColumns, tableColumnOrder: nextColumnOrder });

};

export const handleColumnMoveLeft = (fieldId, fieldIdentifier, fieldPositionId, columnId, tableColumns, tableColumnOrder, onChange) => {

  //Note: Use columns and NOT nextColumns in the getColumnOrder function.
  const nextColumnOrder = tableColumnOrder ? [...tableColumnOrder] : getColumnOrder(tableColumnOrder, tableColumns);
  const columnIndex = nextColumnOrder.indexOf(columnId);

  handleMoveColumnLeft(columnIndex, ({ targetIndex }) => {
    nextColumnOrder[columnIndex] = nextColumnOrder.splice(targetIndex, 1, nextColumnOrder[columnIndex])[0];
    onChange(fieldId, fieldIdentifier, fieldPositionId, { tableColumnOrder: nextColumnOrder });
  });

};

export const handleColumnMoveRight = (fieldId, fieldIdentifier, fieldPositionId, columnId, tableColumns, tableColumnOrder, onChange) => {

  //Note: Use columns and NOT nextColumns in the getColumnOrder function.
  const nextColumnOrder = tableColumnOrder ? [...tableColumnOrder] : getColumnOrder(tableColumnOrder, tableColumns);
  const columnIndex = nextColumnOrder.indexOf(columnId);

  handleMoveColumnRight(nextColumnOrder, columnIndex, ({ targetIndex }) => {
    nextColumnOrder[columnIndex] = nextColumnOrder.splice(targetIndex, 1, nextColumnOrder[columnIndex])[0];
    onChange(fieldId, fieldIdentifier, fieldPositionId, { tableColumnOrder: nextColumnOrder },);
  });

};

export default TableEditorSettings;
